import { Controller } from "stimulus";
import Swal from "sweetalert2";
import VisitDateConsistencyModule from "./shared_modules/visit_date_consistency_module.js";
import NotificationModule from "./shared_modules/notification_module.js";

const UNCERTIFIED_DEVICE = "Uncertified Device";
const UNCERTIFIED_PHOTOGRAPHER = "Uncertified Photographer";

export default class extends Controller {
  static targets = [
    "form",
    "parentSelect",
    "childSelect",
    "deviceModel",
    "deviceName",
    "deviceManufacturer",
    "deviceSerialNumberSelect",
    "deviceSerialNumberInput",
    "deviceSoftwareVersion",
    "photographerSelect",
    "photographerInput",
    "uncertifiedDeviceSection",
    "uncertifiedDeviceTypeSection",
    "uncertifiedDeviceTypeSelect",
    "uncertifiedPhotographerSection",
    "uncertifiedDeviceConfirm",
    "uncertifiedPhotographerConfirm",
    "packageType",
    "packageVerificationWrapper",
    "visitSelect",
    "loadingImage",
  ];

  connect() {
    this.certifiedDevicesBySerial = {};
    this.uncertifiedStudyDevices = [];
    this.togglePackageType();
    this.visitDateConsistency = new VisitDateConsistencyModule();
    this.notifications = new NotificationModule(
      this.hasLoadingImageTarget ? this.loadingImageTarget : null,
    );
    this.showServerValidationErrors();
  }

  isUncertifiedDeviceValue(value) {
    return value === UNCERTIFIED_DEVICE || value === "Others";
  }

  isUncertifiedPhotographerValue(value) {
    return value === UNCERTIFIED_PHOTOGRAPHER || value === "Others";
  }

  showServerValidationErrors() {
    const errorNode = this.element.querySelector("[data-stack-server-errors]");
    if (!errorNode) return;

    let messages = [];
    try {
      messages = JSON.parse(errorNode.textContent.trim());
    } catch (_error) {
      messages = [];
    }

    errorNode.remove();
    if (messages.length === 0) return;

    this.visitDateConsistency.showErrorAlert(
      "Unable to save stack",
      messages.join("<br>"),
      true,
    );
  }

  showValidationAlert(message) {
    return Swal.fire({
      icon: "warning",
      title: message,
    });
  }

  async submitStackForm(form) {
    const submitButton = document.getElementById("stackBtn");
    this.notifications.showLoader();
    if (submitButton) submitButton.disabled = true;

    const stopLoading = () => {
      this.notifications.hideLoader();
      if (submitButton) submitButton.disabled = false;
    };

    try {
      const response = await fetch(form.action, {
        method: form.method?.toUpperCase() || "POST",
        body: new FormData(form),
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        window.location.href =
          data.redirect_url ||
          `/studies/${this.data.get("studyId")}/subjects/${this.data.get("subjectId")}/stacks`;
        return;
      }

      // Hide loader before alerts — it uses z-index 9999 and would block SweetAlert.
      stopLoading();

      if (response.status === 422) {
        const data = await response.json();
        const messages = this.visitDateConsistency.formatRailsErrors(data);
        await this.visitDateConsistency.showErrorAlert(
          "Unable to save stack",
          messages.join("<br>") || "Please review the form and try again.",
          true,
        );
        return;
      }

      await this.visitDateConsistency.showErrorAlert(
        "Unable to save stack",
        "Something went wrong. Please try again.",
      );
    } catch (_error) {
      stopLoading();
      await this.visitDateConsistency.showErrorAlert(
        "Unable to save stack",
        "Something went wrong. Please try again.",
      );
    } finally {
      stopLoading();
    }
  }

  async initialize() {
    this.selectChangeUrl = this.stackChildModalitiesUrl();
    if (this.data.get("mode") == "reload") {
      this.populateChildSelect(true).then(() => {
        this.populatePhotographerSelect();
      });
    }
  }

  stackChildModalitiesUrl() {
    const studyId = this.data.get("studyId");
    const siteId = this.data.get("siteId");
    const modalityId = this.parentSelectTarget.value;
    const baseUrl = `${document.location.origin}/modalities/${modalityId}/stack_child_modalities.json`;
    const params = new URLSearchParams();
    if (studyId) params.set("study_id", studyId);
    if (siteId) params.set("site_id", siteId);
    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
  }

  checkPreviousVisit() {
    const visitId = this.visitSelectTarget.value;
    const modalityId = this.parentSelectTarget.value;
    const studyId = this.data.get("studyId");
    const subjectId = this.data.get("subjectId");
    if (!visitId || !modalityId) return;

    fetch(
      `/studies/${studyId}/visits/check_previous?visit_id=${visitId}&modality_id=${modalityId}&study_id=${studyId}&subject_id=${subjectId}`,
    )
      .then((response) => response.json())
      .then((data) => {
        if (!data.allowed) {
          Swal.fire({
            icon: "warning",
            title: "Missing Stack",
            html: `<b>${data.previous_visit_name}</b> must be created first.`,
            confirmButtonText: "Got it",
            allowOutsideClick: false,
          });
        }
      })
      .catch((err) => console.error(err));
  }

  togglePackageType(event) {
    const checkedRadio =
      event?.target ||
      this.element.querySelector('input[name*="package_verification"]:checked');

    if (checkedRadio && checkedRadio.value === "true") {
      this.packageTypeTarget.classList.remove("hidden");
    } else {
      this.packageTypeTarget.classList.add("hidden");
      if (this.hasPackageTypeTarget) {
        const select = this.packageTypeTarget.querySelector("select");
        if (select) {
          select.selectedIndex = 0;
        }
      }
    }
  }

  handleParentSelectChange() {
    this.resetFormForModalityChange();

    if (this.parentSelectTarget.value != "") {
      this.populateChildSelect().then(() => {
        this.populateSerialNumberSelect();
      });
      this.populatePhotographerSelect();
    }
  }

  resetFormForModalityChange() {
    this.certifiedDevicesBySerial = {};
    this.uncertifiedStudyDevices = [];

    this.hideUncertifiedDeviceSection();
    this.hideUncertifiedPhotographerSection();
    this.clearDeviceFieldValues();
    this.resetUncertifiedDeviceState();

    if (this.hasChildSelectTarget) {
      this.childSelectTarget.innerHTML = "";
      const childDefault = document.createElement("option");
      childDefault.value = "";
      childDefault.innerHTML = "Select Child Modality";
      this.childSelectTarget.appendChild(childDefault);
    }

    const serialSelect = this.getDeviceSerialSelect();
    if (serialSelect) {
      serialSelect.innerHTML = "";
      const serialDefault = document.createElement("option");
      serialDefault.value = "";
      serialDefault.innerHTML = "Select Device Serial Number";
      serialSelect.appendChild(serialDefault);
      delete serialSelect.dataset.selectedSerial;
    }

    if (this.hasPhotographerSelectTarget) {
      this.photographerSelectTarget.innerHTML = "";
      const photographerDefault = document.createElement("option");
      photographerDefault.value = "";
      photographerDefault.innerHTML = "Select Photographer";
      this.photographerSelectTarget.appendChild(photographerDefault);
    }
  }

  populateChildSelect(initialize = false) {
    this.selectChangeUrl = this.stackChildModalitiesUrl();
    const childModalityId = initialize ? this.data.get("childModalityId") : "";

    return fetch(this.selectChangeUrl, {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRF-Token": document.head
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      method: "GET",
    })
      .then((response) => response.json())
      .then((data) => {
        const selectBox = this.childSelectTarget;
        selectBox.innerHTML = "";

        const defaultOption = document.createElement("option");
        defaultOption.value = "";
        defaultOption.innerHTML = "Select Child Modality";
        defaultOption.selected = childModalityId === "";
        selectBox.appendChild(defaultOption);

        data.forEach((item) => {
          const opt = document.createElement("option");
          opt.value = item[0];
          opt.innerHTML = item[1];
          opt.selected = childModalityId === item[0];
          selectBox.appendChild(opt);
        });

        if (initialize) {
          this.syncChildModalityFromSelectedSerial();
        }

        if (initialize) {
          return this.populateSerialNumberSelect(true);
        }
      });
  }

  populateSerialNumberSelect(initialize = false) {
    const modalityId = this.parentSelectTarget.value;
    const studyId = this.data.get("studyId");
    const subjectId = this.data.get("subjectId");
    const persistedSerial = this.data.get("deviceSerialNumber");
    const serialSelect = this.getDeviceSerialSelect();
    if (!serialSelect || modalityId === "") return;

    const selectedSerial = serialSelect.dataset.selectedSerial;
    const certifiedDevicesUrl =
      document.location.origin +
      `/studies/${studyId}/stacks/fetch_certified_devices.json`;

    return fetch(certifiedDevicesUrl, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRF-Token": document.head
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      method: "POST",
      body: JSON.stringify({
        study_id: studyId,
        subject_id: subjectId,
        modality_id: modalityId,
        modality: this.selectedModalityName(),
        site_id: this.data.get("siteId"),
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Failed to fetch certified devices (${response.status})`,
          );
        }
        return response.json();
      })
      .then((certifiedDevices) => {
        if (!Array.isArray(certifiedDevices)) {
          throw new Error("Certified devices response was not an array");
        }

        this.certifiedDevicesBySerial = {};
        serialSelect.innerHTML = "";

        const defaultOption = document.createElement("option");
        defaultOption.value = "";
        defaultOption.innerHTML = "Select Device Serial Number";
        serialSelect.appendChild(defaultOption);

        certifiedDevices.forEach((device) => {
          const serialNo = device.serial_no || device.serialNo;
          if (!serialNo) return;

          this.certifiedDevicesBySerial[serialNo] = device;

          const opt = document.createElement("option");
          opt.value = serialNo;
          opt.innerHTML = `${serialNo} (Certified)`;

          const preferredSerial = this.resolveDeviceSerialForSelection(
            selectedSerial,
            persistedSerial,
          );
          if (preferredSerial && preferredSerial === serialNo) {
            opt.selected = true;
          }

          serialSelect.appendChild(opt);
        });

        const preferredSerial = this.resolveDeviceSerialForSelection(
          selectedSerial,
          persistedSerial,
        );
        if (
          preferredSerial &&
          !this.isUncertifiedDeviceValue(preferredSerial) &&
          !this.certifiedDevicesBySerial[preferredSerial]
        ) {
          const opt = document.createElement("option");
          opt.value = preferredSerial;
          opt.innerHTML = `${preferredSerial} (Certified)`;
          opt.selected = true;
          serialSelect.appendChild(opt);
        }

        const uncertifiedOption = document.createElement("option");
        uncertifiedOption.value = UNCERTIFIED_DEVICE;
        uncertifiedOption.innerHTML = UNCERTIFIED_DEVICE;
        uncertifiedOption.selected =
          this.isUncertifiedDeviceValue(preferredSerial);
        serialSelect.appendChild(uncertifiedOption);

        this.handleDeviceSerialNumberSelectChange(null, initialize);
      })
      .catch((error) => {
        console.error("Error fetching certified devices:", error);
      });
  }

  normalizePersistedSerial(serial) {
    if (serial === "Others") return UNCERTIFIED_DEVICE;
    return serial;
  }

  normalizePersistedPhotographer(name) {
    if (name === "Others") return UNCERTIFIED_PHOTOGRAPHER;
    return name;
  }

  getUncertifiedDeviceInputSerial() {
    if (!this.hasDeviceSerialNumberInputTarget) return "";
    return this.deviceSerialNumberInputTarget.value.trim();
  }

  getUncertifiedPhotographerInputName() {
    if (!this.hasPhotographerInputTarget) return "";
    return this.photographerInputTarget.value.trim();
  }

  findCertifiedDeviceSerial(serial) {
    if (!serial) return null;

    if (this.certifiedDevicesBySerial[serial]) return serial;

    const normalizedInput = serial.toLowerCase();
    return (
      Object.keys(this.certifiedDevicesBySerial).find(
        (certifiedSerial) =>
          certifiedSerial.toLowerCase() === normalizedInput,
      ) || null
    );
  }

  resolveDeviceSerialForSelection(selectedSerial, persistedSerial) {
    const normalized = this.normalizePersistedSerial(
      selectedSerial || persistedSerial,
    );

    if (!this.isUncertifiedDeviceValue(normalized)) {
      return normalized;
    }

    const inputSerial = this.getUncertifiedDeviceInputSerial();
    if (!inputSerial) return normalized;

    return this.findCertifiedDeviceSerial(inputSerial) || normalized;
  }

  resolvePhotographerForSelection(persistedPhotographer, photographerNames) {
    const normalized = this.normalizePersistedPhotographer(persistedPhotographer);

    if (!this.isUncertifiedPhotographerValue(normalized)) {
      return normalized;
    }

    const inputName = this.getUncertifiedPhotographerInputName();
    if (!inputName) return normalized;

    const match = photographerNames.find(
      (name) => name.toLowerCase() === inputName.toLowerCase(),
    );
    return match || normalized;
  }

  handleDeviceSerialNumberSelectChange(_event, initialize = false) {
    const serialSelect = this.getDeviceSerialSelect();
    if (!serialSelect) return;

    const selectedOption = serialSelect.options[serialSelect.selectedIndex];
    const serialValue = selectedOption?.value || "";

    if (this.isUncertifiedDeviceValue(serialValue)) {
      this.showUncertifiedDeviceSection();
      if (!initialize) {
        this.clearDeviceFieldValues();
        this.resetUncertifiedDeviceState();
        this.clearPhotographerSelection();
        this.populateChildSelect(false);
      }
      return;
    }

    this.hideUncertifiedDeviceSection();
    this.resetUncertifiedDeviceState();

    if (serialValue === "") {
      if (!initialize) {
        this.clearDeviceFieldValues();
        this.clearChildModalitySelection();
        this.clearPhotographerSelection();
      }
      return;
    }

    const certifiedDevice = this.certifiedDevicesBySerial[serialValue];
    if (certifiedDevice && !initialize) {
      this.applyCertifiedDeviceValues(certifiedDevice);
      this.syncPhotographerForSerialSelection();
    } else if (certifiedDevice && initialize) {
      this.applyCertifiedDeviceValues(certifiedDevice, true);
    }
  }

  showUncertifiedDeviceSection() {
    if (this.hasUncertifiedDeviceSectionTarget) {
      this.uncertifiedDeviceSectionTarget.classList.remove("hidden");
    }
  }

  hideUncertifiedDeviceSection() {
    if (this.hasUncertifiedDeviceSectionTarget) {
      this.uncertifiedDeviceSectionTarget.classList.add("hidden");
    }
    if (this.hasDeviceSerialNumberInputTarget) {
      this.deviceSerialNumberInputTarget.value = "";
    }
    if (this.hasUncertifiedDeviceConfirmTarget) {
      this.uncertifiedDeviceConfirmTarget.checked = false;
    }
    this.resetUncertifiedDeviceState();
  }

  handleUncertifiedDeviceConfirmChange() {}

  handleChildModalitySelectChange() {
    if (!this.isUncertifiedDeviceSelected()) return;
    this.populateUncertifiedDeviceFromChildModality();
  }

  handleUncertifiedSerialInput() {
    if (!this.isUncertifiedDeviceSelected()) return;
    if (!this.hasChildSelectTarget || !this.childSelectTarget.value) return;
    this.populateUncertifiedDeviceFromChildModality({ softwareOnly: true });
  }

  handleUncertifiedDeviceTypeChange() {
    const deviceId = this.uncertifiedDeviceTypeSelectTarget.value;
    const device = this.uncertifiedStudyDevices.find(
      (entry) => String(entry.id) === String(deviceId),
    );
    if (device) {
      this.applyUncertifiedDevicePayload(device);
    }
  }

  isUncertifiedDeviceSelected() {
    const serialSelect = this.getDeviceSerialSelect();
    return (
      serialSelect && this.isUncertifiedDeviceValue(serialSelect.value)
    );
  }

  async populateUncertifiedDeviceFromChildModality({ softwareOnly = false } = {}) {
    const childModalityId = this.childSelectTarget?.value;
    if (!childModalityId) {
      if (!softwareOnly) this.resetUncertifiedDeviceState();
      return;
    }

    const studyId = this.data.get("studyId");
    const modalityId = this.parentSelectTarget.value;
    const serialNo = this.hasDeviceSerialNumberInputTarget
      ? this.deviceSerialNumberInputTarget.value.trim()
      : "";
    const url =
      document.location.origin +
      `/studies/${studyId}/stacks/fetch_study_devices_for_child_modality.json`;

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json; charset=utf-8",
          "X-CSRF-Token": document.head
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
        },
        method: "POST",
        body: JSON.stringify({
          study_id: studyId,
          subject_id: this.data.get("subjectId"),
          modality_id: modalityId,
          child_modality_id: childModalityId,
          serial_no: serialNo,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch study devices (${response.status})`,
        );
      }

      const data = await response.json();
      this.uncertifiedStudyDevices = data.devices || [];

      if (softwareOnly) {
        const selectedDevice = this.selectedUncertifiedStudyDevice();
        if (this.hasDeviceSoftwareVersionTarget) {
          const version = selectedDevice?.device_software_version;
          if (version) {
            this.applyCatalogDeviceField(
              this.deviceSoftwareVersionTarget,
              version,
            );
          } else {
            this.setFieldReadOnly(this.deviceSoftwareVersionTarget, false);
          }
        }
        return;
      }

      if (this.uncertifiedStudyDevices.length === 0) {
        this.resetUncertifiedDeviceState();
        await this.showValidationAlert(
          "Device details were not found in the study catalog. Please enter Device name, Device model, and Device version manually.",
        );
        return;
      }

      if (this.uncertifiedStudyDevices.length === 1) {
        this.hideUncertifiedDeviceTypeSection();
        this.applyUncertifiedDevicePayload(this.uncertifiedStudyDevices[0]);
        return;
      }

      this.showUncertifiedDeviceTypeSection(this.uncertifiedStudyDevices);
      const autoSelectId = data.auto_select;
      if (autoSelectId) {
        this.uncertifiedDeviceTypeSelectTarget.value = autoSelectId;
        this.handleUncertifiedDeviceTypeChange();
      } else {
        this.clearDeviceFieldValues();
        this.setDeviceFieldsReadOnly(false);
      }
    } catch (error) {
      console.error("Error fetching study devices:", error);
    }
  }

  selectedUncertifiedStudyDevice() {
    if (this.uncertifiedStudyDevices.length === 1) {
      return this.uncertifiedStudyDevices[0];
    }

    if (!this.hasUncertifiedDeviceTypeSelectTarget) return null;

    const deviceId = this.uncertifiedDeviceTypeSelectTarget.value;
    return this.uncertifiedStudyDevices.find(
      (entry) => String(entry.id) === String(deviceId),
    );
  }

  applyUncertifiedDevicePayload(device) {
    this.applyCatalogDeviceField(
      this.hasDeviceModelTarget ? this.deviceModelTarget : null,
      device.model,
    );
    this.applyCatalogDeviceField(
      this.hasDeviceNameTarget ? this.deviceNameTarget : null,
      device.name,
    );
    this.applyCatalogDeviceField(
      this.hasDeviceManufacturerTarget ? this.deviceManufacturerTarget : null,
      device.manufacturer,
    );
    this.applyCatalogDeviceField(
      this.hasDeviceSoftwareVersionTarget
        ? this.deviceSoftwareVersionTarget
        : null,
      device.device_software_version,
    );
  }

  applyCatalogDeviceField(field, value) {
    if (!field) return;

    const filled = (value || "").toString().trim();
    field.value = filled;
    this.setFieldReadOnly(field, filled.length > 0);
  }

  setFieldReadOnly(field, readonly) {
    field.readOnly = readonly;
    field.disabled = false;
    field.classList.toggle("bg-gray-100", readonly);
  }

  setDeviceFieldsReadOnly(readonly) {
    const fields = [
      this.hasDeviceModelTarget ? this.deviceModelTarget : null,
      this.hasDeviceNameTarget ? this.deviceNameTarget : null,
      this.hasDeviceManufacturerTarget ? this.deviceManufacturerTarget : null,
      this.hasDeviceSoftwareVersionTarget
        ? this.deviceSoftwareVersionTarget
        : null,
    ].filter(Boolean);

    fields.forEach((field) => this.setFieldReadOnly(field, readonly));
  }

  showUncertifiedDeviceTypeSection(devices) {
    if (!this.hasUncertifiedDeviceTypeSectionTarget) return;

    this.uncertifiedDeviceTypeSectionTarget.classList.remove("hidden");
    const selectBox = this.uncertifiedDeviceTypeSelectTarget;
    selectBox.innerHTML = "";

    const defaultOption = document.createElement("option");
    defaultOption.value = "";
    defaultOption.innerHTML = "Select Device Type";
    selectBox.appendChild(defaultOption);

    devices.forEach((device) => {
      const opt = document.createElement("option");
      opt.value = device.id;
      opt.innerHTML = device.name;
      selectBox.appendChild(opt);
    });
  }

  hideUncertifiedDeviceTypeSection() {
    if (this.hasUncertifiedDeviceTypeSectionTarget) {
      this.uncertifiedDeviceTypeSectionTarget.classList.add("hidden");
    }
    if (this.hasUncertifiedDeviceTypeSelectTarget) {
      this.uncertifiedDeviceTypeSelectTarget.innerHTML =
        '<option value="">Select Device Type</option>';
    }
  }

  resetUncertifiedDeviceState() {
    this.uncertifiedStudyDevices = [];
    this.hideUncertifiedDeviceTypeSection();
    this.setDeviceFieldsReadOnly(false);
  }

  applyCertifiedDeviceValues(device, preserveExisting = false) {
    this.setDeviceFieldsReadOnly(false);
    const serialNo = device.serial_no || device.serialNo;
    const deviceModel = device.device_model || device.deviceModel;
    const deviceName = device.device_name || device.deviceName;
    const deviceManufacturer =
      device.device_manufacturer || device.deviceManufacturer;
    const deviceSoftwareVersion =
      device.device_software_version || device.deviceSoftwareVersion;
    const childModalityId = device.child_modality_id || device.childModalityId;

    if (
      this.hasDeviceModelTarget &&
      (!preserveExisting || !this.deviceModelTarget.value)
    ) {
      this.deviceModelTarget.value = deviceModel || "";
    }
    if (
      this.hasDeviceNameTarget &&
      (!preserveExisting || !this.deviceNameTarget.value)
    ) {
      this.deviceNameTarget.value = deviceName || "";
    }
    if (
      this.hasDeviceManufacturerTarget &&
      (!preserveExisting || !this.deviceManufacturerTarget.value)
    ) {
      this.deviceManufacturerTarget.value = deviceManufacturer || "";
    }
    if (
      this.hasDeviceSoftwareVersionTarget &&
      (!preserveExisting || !this.deviceSoftwareVersionTarget.value)
    ) {
      this.deviceSoftwareVersionTarget.value = deviceSoftwareVersion || "";
    }

    if (childModalityId && this.hasChildSelectTarget) {
      this.ensureChildModalityOption(
        childModalityId,
        device.child_modality_name || device.childModalityName,
      );
    }
  }

  ensureChildModalityOption(childModalityId, childModalityName) {
    if (!childModalityId || !this.hasChildSelectTarget) return false;

    const normalizedId = String(childModalityId);
    const selectBox = this.childSelectTarget;
    const existingOption = Array.from(selectBox.options).find(
      (option) => option.value === normalizedId,
    );

    if (existingOption) {
      selectBox.value = normalizedId;
      return true;
    }

    // Append child modality from the selected certified device when it is
    // not already in the dropdown (e.g. inactive but still certified).
    const label = (childModalityName || normalizedId).trim();
    if (!label) return false;

    const opt = document.createElement("option");
    opt.value = normalizedId;
    opt.innerHTML = label;
    opt.selected = true;
    selectBox.appendChild(opt);
    return true;
  }

  syncChildModalityFromSelectedSerial() {
    const serialSelect = this.getDeviceSerialSelect();
    if (
      !serialSelect?.value ||
      this.isUncertifiedDeviceValue(serialSelect.value)
    )
      return;

    const certifiedDevice = this.certifiedDevicesBySerial[serialSelect.value];
    if (!certifiedDevice || !this.hasChildSelectTarget) return;

    const childModalityId =
      certifiedDevice.child_modality_id || certifiedDevice.childModalityId;
    const childModalityName =
      certifiedDevice.child_modality_name || certifiedDevice.childModalityName;

    this.ensureChildModalityOption(childModalityId, childModalityName);
  }

  clearDeviceFieldValues() {
    if (this.hasDeviceModelTarget) this.deviceModelTarget.value = "";
    if (this.hasDeviceNameTarget) this.deviceNameTarget.value = "";
    if (this.hasDeviceManufacturerTarget)
      this.deviceManufacturerTarget.value = "";
    if (this.hasDeviceSoftwareVersionTarget) {
      this.deviceSoftwareVersionTarget.value = "";
    }
    this.setDeviceFieldsReadOnly(false);
  }

  clearChildModalitySelection() {
    if (this.hasChildSelectTarget) {
      this.childSelectTarget.value = "";
    }
  }

  clearPhotographerSelection() {
    if (!this.hasPhotographerSelectTarget) return;

    this.photographerSelectTarget.value = "";
    this.handlePhotographerSelectChange();
  }

  syncPhotographerForSerialSelection() {
    if (!this.hasPhotographerSelectTarget) return;

    const certifiedOptions = Array.from(
      this.photographerSelectTarget.options,
    ).filter(
      (option) =>
        option.value && !this.isUncertifiedPhotographerValue(option.value),
    );

    if (certifiedOptions.length === 1) {
      this.photographerSelectTarget.value = certifiedOptions[0].value;
    } else {
      this.photographerSelectTarget.value = "";
    }

    this.handlePhotographerSelectChange();
  }

  shouldSyncPhotographerForCurrentSerial() {
    const serialSelect = this.getDeviceSerialSelect();
    if (!serialSelect) return false;

    const serialValue = serialSelect.value;
    return serialValue && !this.isUncertifiedDeviceValue(serialValue);
  }

  getDeviceSerialSelect() {
    if (this.hasDeviceSerialNumberSelectTarget) {
      return this.deviceSerialNumberSelectTarget;
    }

    return this.element.querySelector(
      '[data-stack-target="deviceSerialNumberSelect"]',
    );
  }

  populatePhotographerSelect() {
    const modalityId = this.parentSelectTarget.value;
    const studyId = this.data.get("studyId");
    const subjectId = this.data.get("subjectId");
    const persistedPhotographer = this.normalizePersistedPhotographer(
      this.data.get("photographer"),
    );

    if (modalityId === "") return;

    const certifiedPhotographersUrl =
      document.location.origin +
      `/studies/${studyId}/stacks/fetch_certified_photographers.json`;

    fetch(certifiedPhotographersUrl, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRF-Token": document.head
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      method: "POST",
      body: JSON.stringify({
        study_id: studyId,
        subject_id: subjectId,
        modality_id: modalityId,
        modality: this.selectedModalityName(),
        site_id: this.data.get("siteId"),
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Failed to fetch certified photographers (${response.status})`,
          );
        }
        return response.json();
      })
      .then((photographers) => {
        const selectBox = this.photographerSelectTarget;
        selectBox.innerHTML = "";

        const photographerNames = (photographers || [])
          .map((name) => name.trim())
          .filter(Boolean);

        const preferredPhotographer = this.resolvePhotographerForSelection(
          persistedPhotographer,
          photographerNames,
        );

        if (
          preferredPhotographer &&
          !this.isUncertifiedPhotographerValue(preferredPhotographer) &&
          !photographerNames.some(
            (name) =>
              name.toLowerCase() === preferredPhotographer.trim().toLowerCase(),
          )
        ) {
          photographerNames.push(preferredPhotographer.trim());
        }

        const defaultOption = document.createElement("option");
        defaultOption.value = "";
        defaultOption.innerHTML = "Select Photographer";
        selectBox.appendChild(defaultOption);

        photographerNames.forEach((name) => {
          const opt = document.createElement("option");
          opt.value = name;
          opt.innerHTML = `${name} (Certified)`;

          if (
            preferredPhotographer &&
            name.toLowerCase() === preferredPhotographer.trim().toLowerCase()
          ) {
            opt.selected = true;
          }

          selectBox.appendChild(opt);
        });

        const uncertifiedOption = document.createElement("option");
        uncertifiedOption.value = UNCERTIFIED_PHOTOGRAPHER;
        uncertifiedOption.innerHTML = UNCERTIFIED_PHOTOGRAPHER;
        uncertifiedOption.selected = this.isUncertifiedPhotographerValue(
          preferredPhotographer,
        );
        selectBox.appendChild(uncertifiedOption);

        this.handlePhotographerSelectChange();

        if (
          this.shouldSyncPhotographerForCurrentSerial() &&
          this.data.get("mode") !== "reload"
        ) {
          this.syncPhotographerForSerialSelection();
        }
      })
      .catch((error) => {
        console.error("Error fetching photographers:", error);
      });
  }

  handlePhotographerSelectChange() {
    const photographerSelectBox =
      this.photographerSelectTarget.options[
        this.photographerSelectTarget.selectedIndex
      ];

    if (this.isUncertifiedPhotographerValue(photographerSelectBox.value)) {
      this.showUncertifiedPhotographerSection();
      return;
    }

    this.hideUncertifiedPhotographerSection();
  }

  showUncertifiedPhotographerSection() {
    if (this.hasUncertifiedPhotographerSectionTarget) {
      this.uncertifiedPhotographerSectionTarget.classList.remove("hidden");
    }
  }

  hideUncertifiedPhotographerSection() {
    if (this.hasUncertifiedPhotographerSectionTarget) {
      this.uncertifiedPhotographerSectionTarget.classList.add("hidden");
    }
    if (this.hasPhotographerInputTarget) {
      this.photographerInputTarget.value = "";
    }
    if (this.hasUncertifiedPhotographerConfirmTarget) {
      this.uncertifiedPhotographerConfirmTarget.checked = false;
    }
  }

  handleUncertifiedPhotographerConfirmChange() {}

  selectedModalityName() {
    if (this.parentSelectTarget.value === "") return "";

    return this.parentSelectTarget.options[
      this.parentSelectTarget.selectedIndex
    ].text;
  }

  modalityRequiresImages() {
    const stackImageViewer = document.querySelector("#stack_image_viewer");
    if (stackImageViewer) {
      return stackImageViewer.value === "true";
    }

    const flagsJson = this.data.get("modalityImageViewerFlags");
    if (!flagsJson) return false;

    try {
      const flags = JSON.parse(flagsJson);
      return flags[this.parentSelectTarget.value] === true;
    } catch (_error) {
      return false;
    }
  }

  isImageUploader() {
    return this.data.get("imageUploader") === "true";
  }

  imagesRequiredForSubmit(deviceSerialValue, photographerValue) {
    if (this.isImageUploader()) return false;

    return (
      this.modalityRequiresImages() ||
      this.isUncertifiedDeviceValue(deviceSerialValue) ||
      this.isUncertifiedPhotographerValue(photographerValue)
    );
  }

  hasAttachedImages(form) {
    const imageInputs = form.querySelectorAll('input[name="stack[images][]"]');
    if (
      Array.from(imageInputs).some((input) => input.value && input.value.trim() !== "")
    ) {
      return true;
    }

    const fileInput = form.querySelector('input[type="file"][name="stack[images][]"]');
    return (fileInput?.files?.length || 0) > 0;
  }

  async formSubmit(event) {
    event.preventDefault();
    let errorMessage = false;

    const deviceSerialSelect = this.getDeviceSerialSelect();
    const deviceSerialValue =
      deviceSerialSelect?.options[deviceSerialSelect.selectedIndex]?.value ||
      "";
    const photographerSelect =
      this.photographerSelectTarget.options[
        this.photographerSelectTarget.selectedIndex
      ];
    const requiredData = document.querySelectorAll(".required-data");

    for (const item of requiredData) {
      if (
        item.value === "" &&
        item.getAttribute("data-label-name") !== null &&
        !errorMessage
      ) {
        await this.showValidationAlert(
          `${item.getAttribute("data-label-name")} field is required.`,
        );
        errorMessage = true;
        break;
      }
    }

    if (!errorMessage) {
      const stack_image_viewer = document.querySelector("#stack_image_viewer");
      if (stack_image_viewer && stack_image_viewer.value === "true") {
        await this.showValidationAlert(
          "Please uncheck the MANDATORY STACK IMAGE UPLOAD from study modality before creating a stack.",
        );
        return;
      }

      if (this.isUncertifiedDeviceValue(deviceSerialValue)) {
        if (this.deviceSerialNumberInputTarget.value.trim() === "") {
          await this.showValidationAlert("Device serial number is required.");
          return;
        }
        if (!this.childSelectTarget.value) {
          await this.showValidationAlert("Child modality is required.");
          return;
        }
        if (
          this.hasUncertifiedDeviceTypeSectionTarget &&
          !this.uncertifiedDeviceTypeSectionTarget.classList.contains("hidden") &&
          !this.uncertifiedDeviceTypeSelectTarget.value
        ) {
          await this.showValidationAlert("Device type is required.");
          return;
        }
        if (
          !this.deviceModelTarget.value.trim() ||
          !this.deviceNameTarget.value.trim() ||
          !this.deviceManufacturerTarget.value.trim() ||
          !this.deviceSoftwareVersionTarget.value.trim()
        ) {
          await this.showValidationAlert(
            "Please enter Device name, Device model, Device manufacturer, and Device software version.",
          );
          return;
        }
        if (
          !this.isImageUploader() &&
          this.hasUncertifiedDeviceConfirmTarget &&
          !this.uncertifiedDeviceConfirmTarget.checked
        ) {
          await this.showValidationAlert(
            "Please confirm that this device is not certified and needs to be submitted for certification.",
          );
          return;
        }
      }

      if (this.isUncertifiedPhotographerValue(photographerSelect.value)) {
        if (this.photographerInputTarget.value.trim() === "") {
          await this.showValidationAlert("Photographer name is required.");
          return;
        }
        if (
          !this.isImageUploader() &&
          this.hasUncertifiedPhotographerConfirmTarget &&
          !this.uncertifiedPhotographerConfirmTarget.checked
        ) {
          await this.showValidationAlert(
            "Please confirm that this photographer is not certified and needs to be submitted for certification.",
          );
          return;
        }
      }

      if (this.imagesRequiredForSubmit(deviceSerialValue, photographerSelect.value)) {
        if (!this.hasAttachedImages(event.target.form)) {
          await this.showValidationAlert("At least one image must be attached.");
          return;
        }
      }

      this.prepareUncertifiedDeviceFormForSubmit();

      const visitId =
        this.hasVisitSelectTarget && this.visitSelectTarget.value
          ? this.visitSelectTarget.value
          : document.querySelector('select[name="stack[visit_id]"]')?.value;
      const visitDateInput =
        document.querySelector('input[data-visitdate="true"]') ||
        document.querySelector('input[name="stack[visit_date]"]');
      const visitDate = visitDateInput?.value;
      const modalityId =
        document.querySelector('select[name="stack[modality_id]"]')?.value ||
        document.querySelector('input[name="stack[modality_id]"]')?.value;

      if (visitId && visitDate && this.data.get("mode") !== "reload") {
        const canProceed = await this.visitDateConsistency.handleCreateSubmit({
          studyId: this.data.get("studyId"),
          subjectId: this.data.get("subjectId"),
          visitId,
          visitDate,
          modalityId,
          excludeStackId: this.data.get("stackId") || null,
        });
        if (!canProceed || !canProceed.proceed) {
          return;
        }
        if (canProceed.reason) {
          this.visitDateConsistency.setDiscrepancyReasonOnForm(
            event.target.form,
            canProceed.reason,
          );
        }
        if (canProceed.confirmVisitDateConflict) {
          this.visitDateConsistency.setConfirmVisitDateConflictOnForm(
            event.target.form,
          );
        }
      }

      await this.submitStackForm(event.target.form);
    }
  }

  prepareUncertifiedDeviceFormForSubmit() {
    const serialSelect = this.getDeviceSerialSelect();
    const serialValue = serialSelect?.value || "";
    if (!this.isUncertifiedDeviceValue(serialValue)) return;

    this.setDeviceFieldsReadOnly(false);
  }

  deleteImage(event) {
    event.preventDefault();
    if (confirm("Are you sure, you want to delete the image?")) {
      const imageElement = event.currentTarget.closest(".w-20.h-20");
      const imageId = imageElement.querySelector(
        'input[name="stack[images_blob_id][]"]',
      ).value;
      const deleteUrlTemplate = event.currentTarget.dataset.stackDeleteImageUrl;
      const deleteUrl = deleteUrlTemplate.replace(":image_id", imageId);
      fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
      })
        .then((response) => {
          if (response.ok) {
            imageElement.remove();
          } else {
            console.error("Failed to delete the image.");
          }
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    }
  }
}
